<template>
  <div>
    <p v-if="summary" class="text-caption text-medium-emphasis mb-2">
      {{ summary }}
    </p>
    <div v-for="group in groups" :key="group.source" class="ai-feedback__group">
      <div v-if="group.title" class="ai-feedback__origin text-caption">
        {{ group.title }}
      </div>
      <ul class="ai-feedback text-body-2">
        <li
          v-for="(point, index) in group.points"
          :key="index"
          class="ai-feedback__point"
          :class="point.severity ? `ai-feedback__point--${point.severity}` : ''"
        >
          <v-icon
            v-if="point.style"
            size="18"
            :color="point.style.color"
            class="ai-feedback__icon"
            aria-hidden="true"
            >{{ point.style.icon }}</v-icon
          >
          <span v-else class="ai-feedback__dot" aria-hidden="true">•</span>
          <span>
            <span
              v-if="point.style"
              class="ai-feedback__label"
              :class="point.style.color ? `text-${point.style.color}` : ''"
              >{{ point.style.label }}:</span
            >
            {{ point.text }}
            <span v-if="point.quote" class="ai-feedback__quote">
              In je tekst: “{{ point.quote }}”
            </span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
/**
 * The feedback points of a text check, grouped by where they come from (the report
 * type, the general checks, the checks of this report type) and marked with how much
 * each matters. Points from older results have no level and show as a plain list.
 */
import { computed } from "vue";
import {
  groupedFeedback,
  severityStyle,
  severitySummary,
} from "@/utils/aiFeedback";

const props = defineProps({
  // [{ text, severity, source?, quote? }] or, for older results, plain strings
  points: { type: Array, default: () => [] },
  // Name of the report type, for the heading of its own checks
  typeName: { type: String, default: "" },
});

const groups = computed(() =>
  groupedFeedback(props.points, { typeName: props.typeName }).map((group) => ({
    ...group,
    points: group.points.map((point) => ({
      ...point,
      style: severityStyle(point.severity),
    })),
  })),
);
const all = computed(() => groups.value.flatMap((group) => group.points));
// Only worth a line when there is more than one point to weigh
const summary = computed(() =>
  all.value.length > 1 ? severitySummary(all.value) : "",
);
</script>

<style scoped>
.ai-feedback {
  list-style: none;
  padding: 0;
  margin: 0;
}

.ai-feedback__group + .ai-feedback__group {
  margin-top: 12px;
}

.ai-feedback__origin {
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 4px;
}

.ai-feedback__point {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 8px;
  border-left: 3px solid transparent;
  border-radius: 4px;
}

.ai-feedback__point + .ai-feedback__point {
  margin-top: 4px;
}

/* The two levels that need action stand out; a point of attention stays quiet. */
.ai-feedback__point--urgent {
  border-left-color: rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), 0.08);
}

.ai-feedback__point--important {
  border-left-color: rgb(var(--v-theme-warning));
  background-color: rgba(var(--v-theme-warning), 0.08);
}

.ai-feedback__icon {
  flex: 0 0 auto;
  margin-top: 1px;
}

.ai-feedback__dot {
  flex: 0 0 18px;
  text-align: center;
}

.ai-feedback__label {
  font-weight: 500;
}

.ai-feedback__quote {
  display: block;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>
